import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// TipTap helpers
export type TiptapJSON = {
  type: string;
  content?: any[];
  [key: string]: any;
};

export const textToTiptapJSON = (text: string): TiptapJSON => {
  const lines = text.split(/\r?\n/);
  return {
    type: 'doc',
    content: lines.map((line) => ({
      type: 'paragraph',
      content: line
        ? [
            {
              type: 'text',
              text: line,
            },
          ]
        : undefined,
    })),
  } as TiptapJSON;
};

export function withOssImageResize(url: string, width = 512): string {
  if (!url) return url;
  if (url.includes('x-oss-process=')) return url;

  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    const path = u.pathname || '';

    // Only apply to our OSS-hosted assets (avoid breaking signed/3rd-party URLs).
    const isLikelyOssHost =
      host.endsWith('aliyuncs.com') ||
      host.endsWith('flowmuse.ai'); // includes cdn/custom domains under flowmuse.ai

    const isLikelyOssObjectPath =
      path.startsWith('/files/') || path.startsWith('/avatars/') || path.startsWith('/screenshots/');

    if (!isLikelyOssHost || !isLikelyOssObjectPath) return url;

    u.searchParams.set('x-oss-process', `image/resize,w_${Math.max(1, Math.floor(width))}`);
    return u.toString();
  } catch {
    // Non-absolute or invalid URL: don't touch it
    return url;
  }
}

export type SupabaseImageTransformOptions = {
  /** 1-2500 */
  width?: number;
  /** 1-2500 */
  height?: number;
  /** default: cover */
  resize?: 'cover' | 'contain' | 'fill';
  /** 20-100 (default 80 in Supabase) */
  quality?: number;
  /** opt-out of webp auto optimization */
  format?: 'origin';
};

function clampInt(n: unknown, min: number, max: number): number | undefined {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return undefined;
  const i = Math.floor(v);
  if (i < min) return min;
  if (i > max) return max;
  return i;
}

/**
 * Supabase Storage Image Transformations
 * - public: /storage/v1/object/public/<bucket>/<path>
 * - render: /storage/v1/render/image/public/<bucket>/<path>?width=&height=&resize=&quality=
 *
 * Docs: `https://supabase.com/docs/guides/storage/image-transformations`
 */
export function withSupabaseImageTransform(
  url: string,
  transform: SupabaseImageTransformOptions
): string {
  if (!url) return url;

  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    if (!host.endsWith('supabase.co')) return url;

    const p = u.pathname || '';

    // Already a render URL; just ensure params are applied.
    const isRender = p.includes('/storage/v1/render/image/');

    const m = !isRender
      ? p.match(/^\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/)
      : p.match(/^\/storage\/v1\/render\/image\/public\/([^/]+)\/(.+)$/);

    if (!m) return url;

    const bucket = m[1];
    const objectPath = m[2];

    u.pathname = `/storage/v1/render/image/public/${bucket}/${objectPath}`;

    // Apply transform params (keep any existing non-transform params untouched).
    const width = clampInt(transform?.width, 1, 2500);
    const height = clampInt(transform?.height, 1, 2500);
    const quality = clampInt(transform?.quality, 20, 100);

    if (width) u.searchParams.set('width', String(width));
    if (height) u.searchParams.set('height', String(height));
    if (transform?.resize) u.searchParams.set('resize', transform.resize);
    if (quality) u.searchParams.set('quality', String(quality));
    if (transform?.format) u.searchParams.set('format', transform.format);

    return u.toString();
  } catch {
    return url;
  }
}

